import { Controller } from "@hotwired/stimulus"

// Member picker with searchable popover.
// Supports single-select (mode="single") and multi-select (mode="multi").
//
// Single mode: selecting a member shows their name in the trigger and closes the popover.
//   Uses radio-style indicators. A clear button appears when a member is selected.
// Multi mode: selecting toggles checkboxes, shows chips above the trigger.
//
// Dispatches "change" event with { emails } detail whenever selection changes.

export default class extends Controller {
  static targets = ["chips", "trigger", "popover", "search", "list", "clearBtn",
    "addEmailRow", "addEmailInput", "addEmailError"]
  static values = {
    members: Array,        // [{ id, name, email, company_name }]
    selected: String,      // space-separated emails (initial selection)
    mode: { type: String, default: "multi" }, // "single" or "multi"
    chipStyle: { type: String, default: "blue" }, // "blue" or "gray"
    placeholder: { type: String, default: "Select..." }
  }

  connect() {
    this._selected = new Set()

    // Parse initial selection
    if (this.selectedValue) {
      this.selectedValue.split(/\s+/).filter(Boolean).forEach(e => this._selected.add(e))
    }

    this._onClickOutside = (e) => {
      // Ticking a row re-renders the list, so by the time this document
      // listener runs the clicked row is detached and no longer "inside"
      // the picker — that phantom outside-click closed the popover after
      // every pick (Larry 2026-09-01: "if I check one, it goes away").
      if (!e.target?.isConnected) return
      if (this.hasPopoverTarget && !this.element.contains(e.target)) {
        this._close()
      }
    }
    document.addEventListener("click", this._onClickOutside)

    this._render()
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
  }

  // ── Public API ──────────────────────────────────────────────────────

  get selectedEmails() {
    return Array.from(this._selected)
  }

  // Set the selection from outside the picker (e.g. Ball in Court following
  // Assigned To). Silent by default so a programmatic default doesn't read
  // as the user having made the choice themselves.
  setSelection(emails, { silent = true } = {}) {
    this._selected = new Set((emails || []).filter(Boolean))
    this._render()
    if (!silent) this._dispatchChange()
  }

  // ── Popover ─────────────────────────────────────────────────────────

  toggle() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.contains("hidden") ? this._open() : this._close()
  }

  _open() {
    this._renderList("")
    this.popoverTarget.classList.remove("hidden")
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }
  }

  _close() {
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
  }

  filter() {
    const q = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
    this._renderList(q)
  }

  _renderList(query) {
    if (!this.hasListTarget) return

    const members = this.membersValue || []
    const isSingle = this.modeValue === "single"
    const filtered = members.filter(m => {
      if (!query) return true
      return `${m.name || ""} ${m.email} ${m.company_name || ""}`.toLowerCase().includes(query)
    })

    let html = ""
    if (filtered.length === 0) {
      html = `<div class="px-3 py-4 text-xs text-gray-400 text-center">${query ? "No matches" : "No members"}</div>`
    } else {
      html = filtered.map(m => {
        const checked = this._selected.has(m.email)
        const hasName = m.name && m.name !== m.email
        const displayName = hasName ? m.name : m.email
        const subtitle = hasName ? m.email : null
        const company = m.company_name || ""

        const indicator = isSingle
          ? `<div class="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${checked ? 'border-brand bg-brand' : 'border-gray-300'}">
              ${checked ? '<svg class="w-full h-full text-white" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="8" r="3"/></svg>' : ''}
            </div>`
          : `<div class="w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${checked ? 'border-brand bg-brand' : 'border-gray-300'}">
              ${checked ? '<svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke-width="3" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/></svg>' : ''}
            </div>`

        return `<div data-action="click->member-multi-select#selectMember"
                     data-email="${this._esc(m.email)}"
                     data-name="${this._esc(m.name || "")}"
                     class="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${checked ? 'bg-brand/5' : ''}">
          ${indicator}
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 truncate">${this._esc(displayName)}</div>
            ${subtitle ? `<div class="text-xs text-gray-400 truncate">${this._esc(subtitle)}</div>` : ''}
            ${company ? `<div class="text-[11px] text-gray-400 truncate">${this._esc(company)}</div>` : ''}
          </div>
        </div>`
      }).join("")
    }

    this.listTarget.innerHTML = html
  }

  // ── Selection ───────────────────────────────────────────────────────

  selectMember(event) {
    const email = event.currentTarget.dataset.email
    const isSingle = this.modeValue === "single"

    if (isSingle) {
      // Single mode: replace selection and close
      this._selected.clear()
      this._selected.add(email)
      this._render()
      this._dispatchChange()
      this._close()
    } else {
      // Multi mode: toggle
      if (this._selected.has(email)) {
        this._selected.delete(email)
      } else {
        this._selected.add(email)
      }
      const query = this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : ""
      this._renderList(query)
      this._renderChips()
      this._updateTrigger()
      this._dispatchChange()
    }
  }

  removeChip(event) {
    const email = event.currentTarget.dataset.email
    this._selected.delete(email)
    this._renderChips()
    this._updateTrigger()
    this._dispatchChange()
  }

  // ── External email (Kloepfer item 7) ────────────────────────────────
  // The view opts in by rendering the addEmailRow targets in the popover:
  // lets the user pick an approver who isn't on the project team. The email
  // joins the member list flagged as "External" and is selected immediately.

  showAddEmail() {
    if (!this.hasAddEmailRowTarget) return
    this.addEmailRowTarget.classList.toggle("hidden")
    if (!this.addEmailRowTarget.classList.contains("hidden") && this.hasAddEmailInputTarget) {
      this.addEmailInputTarget.focus()
    }
  }

  addEmailKeydown(event) {
    if (event.key !== "Enter") return
    event.preventDefault()
    this.submitNewEmail()
  }

  submitNewEmail() {
    if (!this.hasAddEmailInputTarget) return
    const email = this.addEmailInputTarget.value.trim().toLowerCase()
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    if (!valid) {
      if (this.hasAddEmailErrorTarget) {
        this.addEmailErrorTarget.textContent = "Please enter a valid email address."
        this.addEmailErrorTarget.classList.remove("hidden")
      }
      return
    }
    if (this.hasAddEmailErrorTarget) this.addEmailErrorTarget.classList.add("hidden")

    if (!(this.membersValue || []).some(m => m.email === email)) {
      this.membersValue = [...(this.membersValue || []), { id: null, name: "", email, company_name: "External" }]
    }
    this._addSelection(email)
    this.addEmailInputTarget.value = ""
  }

  _addSelection(email) {
    if (this.modeValue === "single") {
      this._selected.clear()
      this._selected.add(email)
      this._render()
      this._dispatchChange()
      this._close()
    } else {
      this._selected.add(email)
      this._renderList(this.hasSearchTarget ? this.searchTarget.value.trim().toLowerCase() : "")
      this._renderChips()
      this._updateTrigger()
      this._dispatchChange()
    }
  }

  clear(event) {
    if (event) event.stopPropagation()
    this._selected.clear()
    this._render()
    this._dispatchChange()
  }

  // ── Rendering ───────────────────────────────────────────────────────

  _render() {
    if (this.modeValue === "single") {
      this._updateTriggerSingle()
    } else {
      this._renderChips()
      this._updateTrigger()
    }
  }

  _updateTriggerSingle() {
    if (!this.hasTriggerTarget) return
    const emails = this.selectedEmails
    if (emails.length === 0) {
      this.triggerTarget.textContent = this.placeholderValue
      this.triggerTarget.classList.add("text-gray-400")
      this.triggerTarget.classList.remove("text-gray-700")
      if (this.hasClearBtnTarget) this.clearBtnTarget.classList.add("hidden")
    } else {
      const email = emails[0]
      const members = this.membersValue || []
      const member = members.find(m => m.email === email)
      const hasName = member?.name && member.name !== email
      const display = hasName ? `${member.name} (${email})` : email
      this.triggerTarget.textContent = display
      this.triggerTarget.classList.remove("text-gray-400")
      this.triggerTarget.classList.add("text-gray-700")
      if (this.hasClearBtnTarget) this.clearBtnTarget.classList.remove("hidden")
    }
  }

  _renderChips() {
    if (!this.hasChipsTarget) return

    const members = this.membersValue || []
    const style = this.chipStyleValue
    // "green" = Ball in Court chips (Phelipe 2026-09-01), so who holds the
    // ball reads differently from who's assigned.
    const chipBg = style === "blue" ? "bg-blue-50 text-blue-700 border-blue-200"
      : style === "green" ? "bg-green-50 text-green-700 border-green-200"
      : "bg-gray-50 text-gray-700 border-gray-200"
    const btnCls = style === "blue" ? "text-blue-400 hover:text-blue-600"
      : style === "green" ? "text-green-400 hover:text-green-600"
      : "text-gray-400 hover:text-gray-600"

    const chips = this.selectedEmails.map(email => {
      const member = members.find(m => m.email === email)
      const label = member?.name || email.split("@")[0]
      return `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium border ${chipBg}" data-email="${this._esc(email)}">
        ${this._esc(label)}
        <button type="button" data-action="click->member-multi-select#removeChip" data-email="${this._esc(email)}" class="${btnCls} cursor-pointer">&times;</button>
      </span>`
    }).join("")

    this.chipsTarget.innerHTML = chips
  }

  _updateTrigger() {
    if (!this.hasTriggerTarget) return
    const count = this._selected.size
    if (count === 0) {
      this.triggerTarget.textContent = this.placeholderValue
      this.triggerTarget.classList.add("text-gray-400")
      this.triggerTarget.classList.remove("text-gray-700")
    } else {
      this.triggerTarget.textContent = `${count} selected`
      this.triggerTarget.classList.remove("text-gray-400")
      this.triggerTarget.classList.add("text-gray-700")
    }
  }

  _dispatchChange() {
    this.dispatch("change", { detail: { emails: this.selectedEmails } })
  }

  // ── Utilities ───────────────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
